import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Send, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Footer } from "@/components/Footer";
import { BackButton } from "@/components/BackButton";

const BOROUGHS = [
  { name: "Manhattan", zips: "10001 – 10282" },
  { name: "Brooklyn", zips: "11201 – 11256" },
  { name: "Queens", zips: "11004 – 11697" },
  { name: "The Bronx", zips: "10451 – 10475" },
  { name: "Staten Island", zips: "10301 – 10314" },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all three fields.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent — we'll get back to you soon.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-md border border-charcoal/20 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-ash/60 focus:border-charcoal";

  return (
    <main data-testid="contact-page" className="pt-24">
      <div className="mx-auto grid max-w-[1600px] grid-cols-1 gap-16 px-6 py-24 sm:px-10 lg:grid-cols-12">
        <div className="lg:col-span-12"><BackButton /></div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-5">
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-taupe">Contact us</p>
          <h1 className="font-serif text-4xl font-light tracking-tighter sm:text-5xl">
            Talk to a real person
          </h1>
          <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ash">
            Questions about a piece, a delivery window, or an order — write to us here.
            A member of our New York team replies personally.
          </p>
          <div className="mt-12 space-y-4 text-sm font-light text-ash">
            <p className="flex items-center gap-3"><Mail size={16} className="text-taupe" /> info@shoponlinenewyork.com</p>
            <p className="flex items-center gap-3"><MapPin size={16} className="text-taupe" /> New York City — delivery across the five boroughs</p>
          </div>
          <div className="mt-14" data-testid="delivery-zone">
            <h2 className="font-serif text-2xl font-light tracking-tight">Where we deliver</h2>
            <p className="mt-3 max-w-md text-sm font-light leading-relaxed text-ash">
              Every order is packed, carried and placed by our own team — which is why we serve
              New York City only, for now. Checkout accepts these ZIP codes:
            </p>
            <div className="mt-8 space-y-0">
              {BOROUGHS.map((b) => (
                <div key={b.name} data-testid={`borough-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="flex items-center justify-between border-t border-charcoal/10 py-4 last:border-b">
                  <span className="flex items-center gap-3 text-sm"><Check size={14} className="text-moss" />{b.name}</span>
                  <span className="text-xs uppercase tracking-[0.15em] text-ash">ZIP {b.zips}</span>
                </div>
              ))}
            </div>
            <p className="mt-6 max-w-md text-xs font-light leading-relaxed text-ash">
              Just outside the city? Enter your ZIP at checkout and leave your email — we'll write the day we reach you.
            </p>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8 }} className="lg:col-span-7">
          <div className="space-y-4 rounded-md border border-charcoal/10 bg-sand/50 p-8 sm:p-12">
            <input data-testid="contact-name" placeholder="Your name" value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="contact-email" type="email" placeholder="Your email" value={form.email} onChange={set("email")} className={inputCls} />
            <textarea data-testid="contact-message" placeholder="How can we help?" rows={6} value={form.message} onChange={set("message")} className={inputCls} />
            <button
              data-testid="contact-submit"
              onClick={submit}
              disabled={sending}
              className="flex items-center gap-3 rounded-full bg-charcoal px-8 py-4 text-xs uppercase tracking-[0.2em] text-bone transition-colors duration-300 hover:bg-moss disabled:opacity-60"
            >
              <Send size={14} /> {sending ? "Sending..." : "Send message"}
            </button>
          </div>
        </motion.div>
      </div>
      <Footer />
    </main>
  );
}
